// B-109 — "Tendencias de la conciliación", in Reportes: coordination reads the trend, not only the
// day. For the month chosen:
//   - the cash differences by region and the ten offices that accumulate the most (absolute pesos,
//     from the amount each difference alert carries — `alert_priority:` in the client's panel config);
//   - the ten offices with the most differences raised;
//   - the open pending notes now, by how long they have been open and by kind, the oldest named;
//   - the billed TX by concept for the six months up to the chosen one (`getCierreBillingSummary`,
//     one call per month, the same figures as "Facturación al banco").
// Drawn only for a client with a day reconciliation: `listCierrePendingItems` answers 404 for any
// other, and then nothing is shown. The pure half is `utils/cierreTendencias.js`.
import { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Spinner, cn } from "@mantis-sas/ui";

import { getCierreBillingSummary, listCierreAlerts, listCierrePendingItems } from "../../../ApiCalls/mantisBusiness";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { formatMoney } from "../../../utils/formatMoney";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { DIAS_VIEJA, textoDias, tipoDePendiente } from "../../../utils/cierreCuadreDia";
import {
  conceptosDelMes,
  diferenciasPorOficina,
  diferenciasPorRegion,
  etiquetaDeMes,
  mesesHasta,
  oficinasConMasAlertas,
  pendientesPorAntiguedad,
  rangoDelMes,
  txPorConceptoYMes,
} from "../../../utils/cierreTendencias";
import { guiaDe } from "../../../utils/cierreTours";

const MESES_TX = 6;
const TONO_BANDA = { hasta3: "var(--brinks-success)", semana: "var(--brinks-warning)", mas7: "var(--brinks-danger)" };

const compacto = (n) => {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1e9) return `${(n / 1e9).toFixed(1)}MM`;
  if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${Math.round(n / 1e3)}k`;
  return formatMoney(n);
};

function Tarjeta({ titulo, subtitulo, children, className }) {
  return (
    <section {...guiaDe(titulo, subtitulo)} className={cn("min-w-0 overflow-hidden rounded-[14px] border bg-card shadow-brinks-card", className)} aria-label={titulo}>
      <header className="border-b px-4 py-3">
        <h3 className="text-[14px] font-semibold text-brinks-foreground">{titulo}</h3>
        {subtitulo ? <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">{subtitulo}</p> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

const Vacio = ({ children }) => (
  <p className="rounded-[10px] border border-dashed py-8 text-center text-[12px] text-brinks-muted-foreground">{children}</p>
);

// The office's name on a horizontal bar: its code and name, cut to fit.
const nombreCorto = (o) => `${o.codigo} ${String(o.oficina || "").slice(0, 18)}`;

// B-138 — `onElegirOficina({ codigo, oficina })`: an office's bar clicked narrows the Dashboard to it.
export function TendenciasConciliacion({ token, mantisBusinessId, clientCode, onElegirOficina = null }) {
  const hoy = useMemo(() => getColombiaDateString(), []);
  const [mes, setMes] = useState(hoy.slice(0, 7));
  const [pendientes, setPendientes] = useState(undefined); // undefined: asking · null: not this client
  const [alertas, setAlertas] = useState(null);
  const [truncado, setTruncado] = useState(false);
  const [facturacion, setFacturacion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [intento, setIntento] = useState(0);
  // An office's bar narrows the Dashboard to it; without a page to narrow, the bars are only drawn.
  const alElegir = onElegirOficina
    ? {
      style: { cursor: "pointer" },
      onClick: (e) => {
        const o = e?.activePayload?.[0]?.payload;
        if (o?.codigo && o.codigo !== "—") onElegirOficina({ codigo: o.codigo, oficina: o.oficina });
      },
    }
    : {};

  // Whether the client has a day reconciliation at all decides the section; the open notes are "now".
  useEffect(() => {
    let activo = true;
    setPendientes(undefined);
    if (!clientCode) return undefined;
    listCierrePendingItems(token, mantisBusinessId, clientCode, { status: "OPEN" })
      .then((r) => {
        if (activo) setPendientes(r?.success ? r.data?.items || [] : null);
      })
      .catch(() => {
        if (activo) setPendientes(null);
      });
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode]);

  // The month's alerts and the six months of billing.
  useEffect(() => {
    if (!clientCode || !pendientes) return undefined;
    let activo = true;
    setCargando(true);
    const { desde, hasta } = rangoDelMes(mes, hoy);
    (async () => {
      const [respuesta, ...meses] = await Promise.all([
        listCierreAlerts(token, mantisBusinessId, clientCode, { since: desde, until: hasta }).catch(() => null),
        ...mesesHasta(mes, MESES_TX).map(async (m) => {
          const rango = rangoDelMes(m, hoy);
          const r = await getCierreBillingSummary(token, mantisBusinessId, clientCode, { from: rango.desde, to: rango.hasta }).catch(() => null);
          return { mes: m, datos: r?.success ? r.data : null };
        }),
      ]);
      if (!activo) return;
      setAlertas(respuesta?.success ? respuesta.data?.alerts || [] : null);
      setTruncado(Boolean(respuesta?.data?.truncated));
      setFacturacion(meses);
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
    // `pendientes` only gates the first load: the month changing is what asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mantisBusinessId, clientCode, mes, intento, pendientes === null || pendientes === undefined]);

  const porRegion = useMemo(() => diferenciasPorRegion(alertas), [alertas]);
  const porOficina = useMemo(() => diferenciasPorOficina(alertas, 10), [alertas]);
  const conMasAlertas = useMemo(() => oficinasConMasAlertas(alertas, 10), [alertas]);
  const antiguedad = useMemo(() => pendientesPorAntiguedad(pendientes, hoy), [pendientes, hoy]);
  const tx = useMemo(() => txPorConceptoYMes(facturacion), [facturacion]);
  // The month the concept ranking reads: the chosen month, or one clicked in the months' bars.
  const [mesTx, setMesTx] = useState(null);
  useEffect(() => setMesTx(null), [mes]);
  const delMes = useMemo(() => conceptosDelMes(tx, mesTx || mes), [tx, mesTx, mes]);
  const maxConcepto = delMes && delMes.conceptos.length ? delMes.conceptos[0].tx : 0;

  if (pendientes === undefined || pendientes === null) return null;

  const totalDiferencias = porRegion.reduce((s, r) => s + r.total, 0);

  return (
    <section className="space-y-3" aria-label="Tendencias de la conciliación">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-bold text-brinks-foreground">Tendencias de la conciliación</h2>
          <p className="text-[12px] text-brinks-muted-foreground">
            Cómo viene el mes: diferencias de efectivo, oficinas con más alertas, notas pendientes y transacciones facturadas.
          </p>
        </div>
        <label className="flex items-center gap-2 text-[12px] text-brinks-muted-foreground">
          Mes
          <input
            type="month"
            aria-label="Mes de las tendencias"
            className="h-8 rounded-[6px] border bg-card px-2 text-[12px] text-brinks-foreground"
            value={mes}
            max={hoy.slice(0, 7)}
            onChange={(e) => e.target.value && setMes(e.target.value)}
          />
        </label>
      </header>

      {truncado ? (
        <p role="status" className="rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-3 py-2 text-[12px] text-brinks-warning">
          El mes tiene más alertas de las que se pueden leer de una vez: las cifras de diferencias y alertas están incompletas.
        </p>
      ) : null}

      {cargando || !facturacion ? (
        <div className="grid place-items-center rounded-[12px] border border-dashed bg-card py-12"><Spinner /></div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          <Tarjeta
            titulo="Diferencias de efectivo del mes"
            subtitulo={alertas
              ? `${montoLegible(totalDiferencias)} en ${porRegion.reduce((s, r) => s + r.veces, 0)} diferencias, sin compensar sobrantes con faltantes · ${etiquetaDeMes(mes)}`
              : "No se pudieron leer las alertas del mes."}
          >
            {porRegion.length ? (
              <div className="space-y-3">
                <ul className="max-h-48 space-y-1.5 overflow-y-auto pr-1" aria-label="Diferencias por región">
                  {porRegion.map((r) => (
                    <li key={r.region} className="text-[12px]">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{r.region}</span>
                        <span className="tabular-nums">{montoLegible(r.total)}</span>
                      </div>
                      <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-brinks-surface">
                        <div className="h-full rounded-full bg-brinks-danger" style={{ width: `${totalDiferencias ? (r.total / totalDiferencias) * 100 : 0}%` }} />
                      </div>
                      <span className="text-[10.5px] text-brinks-muted-foreground">
                        {r.veces} {r.veces === 1 ? "diferencia" : "diferencias"} en {r.oficinas} {r.oficinas === 1 ? "oficina" : "oficinas"}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="h-64" aria-label="Oficinas con más diferencia">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={porOficina.map((o) => ({ ...o, nombre: nombreCorto(o) }))} layout="vertical" margin={{ left: 8, right: 16 }} {...alElegir}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" horizontal={false} />
                      <XAxis type="number" tickFormatter={compacto} tickLine={false} axisLine={false} fontSize={11} />
                      <YAxis type="category" dataKey="nombre" width={130} tickLine={false} axisLine={false} fontSize={10.5} />
                      <Tooltip formatter={(v) => montoLegible(v)} cursor={{ fill: "var(--brinks-surface)" }} />
                      <Bar dataKey="total" name="Diferencia" fill="var(--brinks-danger)" radius={[0, 4, 4, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : alertas ? <Vacio>No hubo diferencias de efectivo con monto en el mes.</Vacio> : null}
          </Tarjeta>

          <Tarjeta titulo="Oficinas con más alertas" subtitulo={`Diferencias levantadas en ${etiquetaDeMes(mes)} (sin lo que solo está pendiente de llegar)${onElegirOficina ? ". Toca una oficina para ver solo esa en el Dashboard" : ""}`}>
            {conMasAlertas.length ? (
              <div className="h-[22rem]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conMasAlertas.map((o) => ({ ...o, nombre: nombreCorto(o) }))} layout="vertical" margin={{ left: 8, right: 16 }} {...alElegir}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
                    <YAxis type="category" dataKey="nombre" width={130} tickLine={false} axisLine={false} fontSize={10.5} />
                    <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                    <Bar dataKey="alertas" name="Alertas" fill="var(--brinks-warning)" radius={[0, 4, 4, 0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : alertas ? <Vacio>Ninguna oficina tuvo alertas en el mes.</Vacio> : (
              <div role="alert" className="space-y-2 rounded-[10px] border border-dashed py-6 text-center text-[12px] text-brinks-muted-foreground">
                <p>No se pudieron leer las alertas del mes.</p>
                <button
                  type="button"
                  className="rounded-[6px] border bg-card px-3 py-1 text-[12px] text-brinks-foreground hover:bg-brinks-surface-hover"
                  onClick={() => setIntento((n) => n + 1)}
                >
                  Reintentar
                </button>
              </div>
            )}
          </Tarjeta>

          <Tarjeta
            titulo="Notas pendientes abiertas"
            subtitulo={`${antiguedad.cantidad} abiertas hoy por ${montoLegible(antiguedad.monto)}`}
          >
            {antiguedad.cantidad ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-[11.5px] font-semibold text-brinks-muted-foreground">Por antigüedad</p>
                  <ul className="space-y-1.5" aria-label="Pendientes por antigüedad">
                    {antiguedad.bandas.map((b) => (
                      <li key={b.key} className="text-[12px]">
                        <div className="flex justify-between gap-2">
                          <span>{b.etiqueta}</span>
                          <span className="tabular-nums font-semibold">{b.cantidad}</span>
                        </div>
                        <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-brinks-surface">
                          <div className="h-full rounded-full" style={{ width: `${(b.cantidad / antiguedad.cantidad) * 100}%`, background: TONO_BANDA[b.key] }} />
                        </div>
                        <span className="text-[10.5px] tabular-nums text-brinks-muted-foreground">{montoLegible(b.monto)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mb-1 mt-3 text-[11.5px] font-semibold text-brinks-muted-foreground">Por tipo</p>
                  <ul className="max-h-40 space-y-0.5 overflow-y-auto pr-1 text-[12px]" aria-label="Pendientes por tipo">
                    {antiguedad.porTipo.map((t) => (
                      <li key={t.tipo} className="flex justify-between gap-2">
                        <span>{t.etiqueta}</span>
                        <span className="tabular-nums">{t.cantidad} · {montoLegible(t.monto)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="mb-1 text-[11.5px] font-semibold text-brinks-muted-foreground">Las más antiguas</p>
                  <ul className="max-h-72 divide-y overflow-y-auto pr-1 text-[12px]" aria-label="Pendientes más antiguos">
                    {antiguedad.masViejas.map((i) => (
                      <li key={i.id} className="py-1.5">
                        <span className="block font-medium">
                          <span className="tabular-nums text-brinks-muted-foreground">{i.officeCode}</span> {i.officeName || ""}
                        </span>
                        <span className="flex justify-between gap-2 text-[11px] text-brinks-muted-foreground">
                          <span>{tipoDePendiente(i.kind)} · desde {fechaDMY(i.openedOn)}</span>
                          <span className={cn("tabular-nums", i.dias > DIAS_VIEJA && "font-semibold text-brinks-warning")}>{textoDias(i.dias)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : <Vacio>No hay notas pendientes abiertas.</Vacio>}
          </Tarjeta>

          <Tarjeta
            titulo="TX facturadas por concepto y mes"
            subtitulo={`Los ${MESES_TX} meses hasta ${etiquetaDeMes(mes)}, sin provisión ni notas. Toca un mes para ver sus conceptos.`}
          >
            {tx.conceptos.length ? (
              // Nine concepts stacked in six bars could not be read: one total per month instead
              // (a month is clicked to read it), and that month's concepts as a ranking.
              <div className="space-y-4">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-[10.5px] font-medium uppercase tracking-wide text-brinks-muted-foreground">
                      TX facturadas · {delMes ? delMes.etiqueta : etiquetaDeMes(mesTx || mes)}
                    </p>
                    <p className="mt-0.5 font-lora text-[26px] font-semibold leading-none tabular-nums text-brinks-foreground">
                      {delMes ? formatMoney(delMes.total) : "—"}
                    </p>
                  </div>
                  {delMes && delMes.variacion !== null ? (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-semibold tabular-nums",
                        delMes.variacion >= 0 ? "bg-brinks-success-soft text-brinks-success" : "bg-brinks-danger-soft text-brinks-danger"
                      )}
                    >
                      {delMes.variacion >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                      {delMes.variacion > 0 ? "+" : ""}{delMes.variacion}% vs. mes anterior
                    </span>
                  ) : null}
                </div>

                <div className="h-28" aria-label="TX por mes">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tx.puntos} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                      <XAxis dataKey="etiqueta" tickLine={false} axisLine={false} fontSize={11} />
                      <YAxis hide />
                      <Tooltip formatter={(v) => [formatMoney(v), "TX"]} cursor={{ fill: "var(--brinks-surface)" }} />
                      <Bar
                        dataKey="total"
                        name="TX"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={40}
                        className="cursor-pointer"
                        onClick={(d) => d && d.mes && setMesTx(d.mes)}
                      >
                        {tx.puntos.map((p) => (
                          <Cell key={p.mes} fill={p.mes === (delMes ? delMes.mes : mesTx || mes) ? "hsl(var(--primary))" : "var(--brinks-border)"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {delMes && delMes.conceptos.length ? (
                  <ul className="max-h-64 space-y-2.5 overflow-y-auto pr-1" aria-label="TX por concepto">
                    {delMes.conceptos.map((c) => (
                      <li key={c.key} className="text-[12px]">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="truncate font-medium text-brinks-foreground">{c.label}</span>
                          <span className="shrink-0 tabular-nums">
                            <span className="font-semibold text-brinks-foreground">{formatMoney(c.tx)}</span>
                            <span className="ml-1.5 text-brinks-muted-foreground">{c.share}%</span>
                            {c.delta !== null && c.delta !== 0 ? (
                              <span className={cn("ml-1.5 text-[11px]", c.delta > 0 ? "text-brinks-success" : "text-brinks-danger")}>
                                {c.delta > 0 ? "▲" : "▼"} {formatMoney(Math.abs(c.delta))}
                              </span>
                            ) : null}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-brinks-surface">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${maxConcepto ? (c.tx / maxConcepto) * 100 : 0}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Vacio>{delMes ? "No hubo transacciones facturadas en ese mes." : "No se pudo leer la facturación de ese mes."}</Vacio>
                )}
              </div>
            ) : <Vacio>No hay transacciones facturadas en esos meses.</Vacio>}
          </Tarjeta>
        </div>
      )}
    </section>
  );
}

export default TendenciasConciliacion;
